import { api } from "../../lib";

export type PanelWorkflowKind = "ordinary" | "tissue_cypher";
export type PanelBillingMode =
  | "group_only"
  | "group_plus_individual"
  | "individual_only";
export type PanelComponentBillingPolicy = "standard" | "none" | "override";
export type PanelSortField = "name" | "updatedAt" | "currentVersionNumber";
export type SortOrder = "asc" | "desc";

export interface PanelCptCodeSummary {
  id: string;
  code: string;
  description: string;
}

export interface PanelStainSummary {
  id: string;
  code: string;
  description: string;
  stainType: string;
}

export interface PanelComponent {
  id: string;
  stainId: string;
  quantity: number;
  displaySequence: number;
  billingPolicy: PanelComponentBillingPolicy;
  overrideCptId: string | null;
  stain: PanelStainSummary;
  overrideCptCode: PanelCptCodeSummary | null;
}

export interface PanelDefinition {
  id: string;
  name: string;
  description: string | null;
  systemKey: string | null;
  isSystemManaged: boolean;
  isActive: boolean;
  workflowKind: PanelWorkflowKind;
  currentVersionNumber: number;
  billingMode: PanelBillingMode;
  groupCptId: string | null;
  groupCptCode: PanelCptCodeSummary | null;
  components: PanelComponent[];
  createdAt: string;
  updatedAt: string;
}

export interface PanelListParams {
  page: number;
  limit: number;
  search?: string;
  status?: "active" | "inactive";
  workflowKind?: PanelWorkflowKind;
  sortBy: PanelSortField;
  sortOrder: SortOrder;
}

export interface PaginatedPanels {
  data: PanelDefinition[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PanelComponentInput {
  stainId: string;
  quantity: number;
  billingPolicy: PanelComponentBillingPolicy;
  overrideCptId: string | null;
}

export interface SavePanelInput {
  name: string;
  description: string | null;
  workflowKind: PanelWorkflowKind;
  billingMode: PanelBillingMode;
  groupCptId: string | null;
  components: PanelComponentInput[];
}

export class PanelAdminRequestError extends Error {}

function getResponseStatus(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return undefined;
  }
  const response = (error as { response?: { status?: number } }).response;
  return response?.status;
}

function translatePanelAdminError(error: unknown): PanelAdminRequestError {
  const status = getResponseStatus(error);
  if (status === 403) {
    return new PanelAdminRequestError(
      "You do not have permission to manage panels."
    );
  }
  if (status === 409) {
    return new PanelAdminRequestError(
      "This panel conflicts with the current configuration. Refresh and review its name or status."
    );
  }
  if (status === 422 || status === 400) {
    return new PanelAdminRequestError(
      "The panel could not be saved. Review its billing and component settings."
    );
  }
  return new PanelAdminRequestError(
    "Panels are unavailable right now. Refresh and try again."
  );
}

async function requestPanels(
  params: PanelListParams
): Promise<PaginatedPanels> {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      searchParams.set(key, String(value));
    }
  });
  return api.get<PaginatedPanels>(`/admin/panels?${searchParams.toString()}`);
}

async function requestPanel(id: string): Promise<PanelDefinition> {
  return api.get<PanelDefinition>(`/admin/panels/${id}`);
}

async function createPanel(input: SavePanelInput): Promise<PanelDefinition> {
  return api.post<PanelDefinition>("/admin/panels", input);
}

async function createPanelVersion(
  id: string,
  input: SavePanelInput
): Promise<PanelDefinition> {
  return api.put<PanelDefinition>(`/admin/panels/${id}`, input);
}

async function updatePanelStatus(
  id: string,
  isActive: boolean
): Promise<PanelDefinition> {
  return api.patch<PanelDefinition>(`/admin/panels/${id}/status`, {
    isActive,
  });
}

async function translateRequest<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (error) {
    throw translatePanelAdminError(error);
  }
}

export function getPanelAdminErrorMessage(error: unknown): string {
  return error instanceof PanelAdminRequestError
    ? error.message
    : "Panels are unavailable right now. Refresh and try again.";
}

export const panelsService = {
  list: (params: PanelListParams) => translateRequest(requestPanels(params)),
  getById: (id: string) => translateRequest(requestPanel(id)),
  create: (input: SavePanelInput) => translateRequest(createPanel(input)),
  createVersion: (id: string, input: SavePanelInput) =>
    translateRequest(createPanelVersion(id, input)),
  updateStatus: (id: string, isActive: boolean) =>
    translateRequest(updatePanelStatus(id, isActive)),
};
